import type { Maybe } from 'graphql/jsutils/Maybe';

import type { Navigation, NavigationItem } from '~/utilities/graphql/codegen';
import { removeTagsFromHTML } from '~/utilities/parsers';

export type AccountMenuSubLink = {
  uid?: string;
  label: string;
  url: string;
};

export type AccountMenuItem = {
  uid?: string;
  label: string;
  url?: string;
  children?: AccountMenuSubLink[];
};

export type AccountMenuAccessibilityLabels = {
  myAccountIcon: string;
  myAccountMenu: string;
};

export type AccountMenuData = {
  logInLinksText: AccountMenuItem[];
  a11y: AccountMenuAccessibilityLabels;
};

export type LocalizedAccountMenuContent = {
  logInLinksText: {
    logIn: string;
    register: string;
  };
  a11y: AccountMenuAccessibilityLabels;
};

export type LocalizedAccountMenuData = Record<string, LocalizedAccountMenuContent>;

export const ACCOUNT_MENU_URLS = {
  account: '/account',
  logIn: '/account/create',
  register: '/account/register',
} as const;

export const localizedAccountMenuContent: LocalizedAccountMenuData = {
  en: {
    logInLinksText: {
      logIn: 'Log in',
      register: 'Create account',
    },
    a11y: {
      myAccountIcon: 'Go to My Account page',
      myAccountMenu: 'Account options',
    },
  },
  nl: {
    logInLinksText: {
      logIn: 'Inloggen',
      register: 'Account aanmaken',
    },
    a11y: {
      myAccountIcon: 'Ga naar Mijn Account',
      myAccountMenu: 'Accountopties',
    },
  },
  de: {
    logInLinksText: {
      logIn: 'Anmelden',
      register: 'Konto erstellen',
    },
    a11y: {
      myAccountIcon: 'Zur Kontoseite',
      myAccountMenu: 'Kontooptionen',
    },
  },
  fr: {
    logInLinksText: {
      logIn: 'Connexion',
      register: 'Créer un compte',
    },
    a11y: {
      myAccountIcon: 'Aller à la page Mon compte',
      myAccountMenu: 'Options du compte',
    },
  },
  es: {
    logInLinksText: {
      logIn: 'Iniciar sesión',
      register: 'Crear cuenta',
    },
    a11y: {
      myAccountIcon: 'Ir a la página Mi cuenta',
      myAccountMenu: 'Opciones de cuenta',
    },
  },
  ja: {
    logInLinksText: {
      logIn: 'ログイン',
      register: '新規登録',
    },
    a11y: {
      myAccountIcon: 'マイアカウントページへ',
      myAccountMenu: 'アカウントオプション',
    },
  },
  it: {
    logInLinksText: {
      logIn: 'Accedi',
      register: 'Crea account',
    },
    a11y: {
      myAccountIcon: 'Vai alla pagina Il mio account',
      myAccountMenu: 'Opzioni account',
    },
  },
  pl: {
    logInLinksText: {
      logIn: 'Zaloguj się',
      register: 'Utwórz konto',
    },
    a11y: {
      myAccountIcon: 'Przejdź do strony Moje konto',
      myAccountMenu: 'Opcje konta',
    },
  },
  bg: {
    logInLinksText: {
      logIn: 'Вход',
      register: 'Създай профил',
    },
    a11y: {
      myAccountIcon: 'Отиди на страница "Моят профил"',
      myAccountMenu: 'Опции за профила',
    },
  },
};

export const getAccountMenuContent = (language?: string): AccountMenuData => {
  const normalizedLanguage = language?.toLowerCase().split(/[_-]/)[0];
  const data =
    (normalizedLanguage ? localizedAccountMenuContent[normalizedLanguage] : undefined) ??
    localizedAccountMenuContent.en;

  return {
    logInLinksText: [
      {
        label: data.logInLinksText.logIn,
        url: ACCOUNT_MENU_URLS.logIn,
      },
      {
        label: data.logInLinksText.register,
        url: ACCOUNT_MENU_URLS.register,
      },
    ],
    a11y: data.a11y,
  };
};

/**
 * Whether a root node of the left navigation is the "My account" node.
 */
export const isAccountNavigationNode = (item?: Maybe<NavigationItem>): boolean => {
  if (!item) {
    return false;
  }

  if (item.uid === 'myAccountNode' || item.uid === 'staticMyAccount_navigationNode') {
    return true;
  }

  const labelLower = item.label?.trim().toLowerCase();

  return labelLower === 'my account' || labelLower === 'mijn account';
};

export const findAccountNavigationNode = (
  items?: Maybe<Maybe<NavigationItem>[]>
): Maybe<NavigationItem> | undefined =>
  items?.find(isAccountNavigationNode) ??
  items?.find(item => item?.hideForNonLoggedInVisitors === true);

export const extractAccountLinksFromNavigation = (
  navigation?: Maybe<Navigation>
): { title?: string; links: AccountMenuItem[] } | null => {
  if (!navigation?.navigation || navigation.navigation.length === 0) {
    return null;
  }

  const accountNode = findAccountNavigationNode(navigation.navigation);

  if (!accountNode?.children || accountNode.children.length === 0) {
    return null;
  }

  const links: AccountMenuItem[] = accountNode.children
    .filter(
      (child): child is NonNullable<typeof child> =>
        Boolean(child) && !child?.hideForLoggedInVisitors
    )
    .reduce<AccountMenuItem[]>((acc, child) => {
      const cleanLabel = removeTagsFromHTML(child.label ?? '')?.trim() ?? '';

      if (!cleanLabel) {
        return acc;
      }

      if (child.children && child.children.length > 0) {
        const subLinks: AccountMenuSubLink[] = child.children
          .filter(
            (sub): sub is NonNullable<typeof sub> => Boolean(sub) && !sub?.hideForLoggedInVisitors
          )
          .reduce<AccountMenuSubLink[]>((subAcc, sub) => {
            const cleanSubLabel = removeTagsFromHTML(sub.label ?? '')?.trim() ?? '';

            if (cleanSubLabel && sub.url) {
              subAcc.push({
                uid: sub.uid ?? undefined,
                label: cleanSubLabel,
                url: sub.url,
              });
            }

            return subAcc;
          }, []);

        acc.push({
          uid: child.uid ?? undefined,
          label: cleanLabel,
          url: child.url ?? undefined,
          children: subLinks.length > 0 ? subLinks : undefined,
        });
      } else {
        acc.push({
          uid: child.uid ?? undefined,
          label: cleanLabel,
          url: child.url ?? undefined,
        });
      }

      return acc;
    }, []);

  return links.length > 0
    ? {
        title: removeTagsFromHTML(accountNode.label ?? '')?.trim() || undefined,
        links,
      }
    : null;
};
